import React from "react";
import { X, Minus, Plus, ShoppingBag, Trash2, Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatMoney } from "@/lib/shopify";

export default function CartDrawer() {
  const {
    cart,
    isOpen,
    closeCart,
    setLineQuantity,
    removeItem,
    checkout,
    busy,
  } = useCart();

  const lines = cart?.lines || [];
  const isEmpty = lines.length === 0;

  return (
    <>
      {/* Overlay */}
      <div
        aria-hidden={!isOpen}
        onClick={closeCart}
        className={`fixed inset-0 z-[70] bg-onyx/70 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />

      {/* Panel */}
      <aside
        data-testid="cart-drawer"
        aria-label="Shopping cart"
        className={`fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col bg-onyx border-l border-gold/25 transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex items-center justify-between border-b border-gold/20 px-6 py-5">
          <div className="flex items-center gap-3">
            <ShoppingBag size={18} className="text-gold" />
            <h2 className="font-display text-2xl text-ivory">Your Bag</h2>
          </div>
          <button
            type="button"
            data-testid="cart-close-btn"
            onClick={closeCart}
            aria-label="Close cart"
            className="inline-flex h-10 w-10 items-center justify-center text-gold hover:text-gold/80"
          >
            <X size={24} />
          </button>
        </div>

        {isEmpty ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
            <ShoppingBag size={44} className="text-gold/40" />
            <p className="text-muted-ivory">Your bag is empty.</p>
            <button
              type="button"
              onClick={closeCart}
              className="btn-gold-outline px-7 py-3 text-[0.7rem] tracking-[0.24em] uppercase"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <ul className="space-y-6">
                {lines.map((line) => (
                  <li key={line.id} className="flex gap-4" data-testid={`cart-line-${line.id}`}>
                    <div className="h-20 w-20 shrink-0 overflow-hidden border border-gold/20 bg-charcoal">
                      {line.image?.url && (
                        <img
                          src={line.image.url}
                          alt={line.image.altText || line.productTitle}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex flex-1 flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-ivory">{line.productTitle}</p>
                        <button
                          type="button"
                          onClick={() => removeItem(line.id)}
                          aria-label="Remove item"
                          disabled={busy}
                          className="text-muted-ivory hover:text-gold"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      {line.variantTitle && line.variantTitle !== "Default Title" && (
                        <p className="mt-0.5 text-xs text-muted-ivory">{line.variantTitle}</p>
                      )}
                      <div className="mt-auto flex items-center justify-between pt-3">
                        <div className="flex items-center border border-gold/30">
                          <button
                            type="button"
                            aria-label="Decrease quantity"
                            disabled={busy}
                            onClick={() => setLineQuantity(line.id, line.quantity - 1)}
                            className="flex h-8 w-8 items-center justify-center text-gold hover:bg-gold/10"
                          >
                            <Minus size={14} />
                          </button>
                          <span className="w-8 text-center text-sm text-ivory">{line.quantity}</span>
                          <button
                            type="button"
                            aria-label="Increase quantity"
                            disabled={busy}
                            onClick={() => setLineQuantity(line.id, line.quantity + 1)}
                            className="flex h-8 w-8 items-center justify-center text-gold hover:bg-gold/10"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                        <span className="text-sm text-gold">
                          {line.lineTotal &&
                            formatMoney(line.lineTotal.amount, line.lineTotal.currencyCode)}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-gold/20 px-6 py-6">
              <div className="flex items-center justify-between text-sm">
                <span className="uppercase tracking-[0.2em] text-muted-ivory">Subtotal</span>
                <span className="text-lg text-ivory" data-testid="cart-subtotal">
                  {cart?.subtotal &&
                    formatMoney(cart.subtotal.amount, cart.subtotal.currencyCode)}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-ivory">
                Shipping & taxes calculated at checkout.
              </p>
              <button
                type="button"
                data-testid="checkout-btn"
                onClick={checkout}
                disabled={busy}
                className="btn-gold mt-5 flex w-full items-center justify-center gap-2 px-6 py-4 text-[0.72rem] tracking-[0.24em] uppercase disabled:opacity-60"
              >
                <Lock size={14} /> Secure Checkout
              </button>
              <button
                type="button"
                onClick={closeCart}
                className="mt-3 w-full text-center text-[0.68rem] uppercase tracking-[0.22em] text-muted-ivory hover:text-gold"
              >
                Continue Shopping
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
